import React, { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Key, Save, RefreshCw, Loader2, Eye, EyeOff, Trash2, ShieldCheck } from "lucide-react";

const FIELDS = [
  { key: "stripe_test_secret_key", label: "Test Secret Key", placeholder: "sk_test_…" },
  { key: "stripe_test_publishable_key", label: "Test Publishable Key", placeholder: "pk_test_…" },
  { key: "stripe_test_webhook_secret", label: "Test Webhook Signing Secret", placeholder: "whsec_…" },
  { key: "stripe_live_secret_key", label: "Live Secret Key", placeholder: "sk_live_… or rk_live_…" },
  { key: "stripe_live_publishable_key", label: "Live Publishable Key", placeholder: "pk_live_…" },
  { key: "stripe_live_webhook_secret", label: "Live Webhook Signing Secret", placeholder: "whsec_…" },
];

const SourceBadge = ({ source }) => {
  const map = {
    db: { label: "Custom (DB)", color: "#6dd4a0", bg: "rgba(45,184,145,0.12)" },
    env: { label: ".env fallback", color: "#c9a84c", bg: "rgba(201,168,76,0.12)" },
    none: { label: "Not set", color: "#ff9a8a", bg: "rgba(224,80,64,0.12)" },
  };
  const m = map[source] || map.none;
  return (
    <span style={{
      fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
      padding: "2px 8px", borderRadius: 999, color: m.color, background: m.bg,
      border: `1px solid ${m.color}40`, fontFamily: "var(--font-mono, monospace)",
    }}>{m.label}</span>
  );
};

export default function StripeKeysPanel() {
  const [keys, setKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [show, setShow] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/stripe-keys");
      setKeys(data || {});
      setDrafts({});
    } catch (e) {
      toast.error("Could not load Stripe keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setDraft = (k, v) => setDrafts((d) => ({ ...d, [k]: v }));
  const dirtyCount = Object.values(drafts).filter((v) => (v ?? "").trim() !== "").length;

  const save = async () => {
    if (dirtyCount === 0) return toast.info("Nothing to save");
    setSaving(true);
    try {
      const payload = {};
      Object.entries(drafts).forEach(([k, v]) => {
        if ((v ?? "").trim() !== "") payload[k] = v.trim();
      });
      const { data } = await api.put("/api/admin/stripe-keys", payload);
      toast.success(`${dirtyCount} key${dirtyCount > 1 ? "s" : ""} updated. Powering Stripe immediately.`);
      setKeys(data || {});
      setDrafts({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const clearOne = async (field) => {
    if (!window.confirm(`Clear the custom value for "${field}" and revert to the .env fallback?`)) return;
    try {
      const { data } = await api.delete(`/api/admin/stripe-keys/${field}`);
      toast.success("Reverted to .env fallback");
      setKeys(data || {});
      setDrafts((d) => ({ ...d, [field]: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <div className="vbs-card" style={{ padding: 26, marginBottom: 22 }} data-testid="stripe-keys-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Key size={18} style={{ color: "var(--gold)" }} />
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "var(--bright)" }}>
          Stripe <em style={{ color: "var(--gold)" }}>API Keys</em>
        </div>
        <button
          onClick={load}
          className="vbs-btn vbs-btn-ghost"
          style={{ marginLeft: "auto", padding: "6px 12px", fontSize: 11 }}
          disabled={loading}
          data-testid="stripe-keys-refresh"
        >
          <RefreshCw size={11} /> Refresh
        </button>
      </div>
      <p style={{ fontSize: 13, color: "var(--mid)", marginBottom: 18, lineHeight: 1.65 }}>
        Override the .env keys without redeploying. Anything left blank keeps using the .env fallback.
        Changes take effect on the very next API call.
        Get your keys at{" "}
        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" style={{ color: "var(--gold)" }}>
          dashboard.stripe.com/apikeys
        </a>.
      </p>

      {loading ? (
        <div style={{ padding: 30, textAlign: "center", color: "var(--gray)" }}>
          <Loader2 className="animate-spin" size={18} style={{ marginRight: 8, verticalAlign: "-3px" }} />
          Loading keys…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {FIELDS.map((f) => {
            const meta = keys[f.key] || { masked: "", source: "none" };
            const isShown = !!show[f.key];
            const draft = drafts[f.key] ?? "";
            return (
              <div
                key={f.key}
                style={{
                  border: "1px solid var(--border)", borderRadius: 4,
                  padding: "12px 14px",
                  background: meta.source === "db" ? "rgba(45,184,145,0.04)" : "transparent",
                }}
                data-testid={`stripe-key-row-${f.key}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "var(--bright)", fontWeight: 500 }}>{f.label}</span>
                  <SourceBadge source={meta.source} />
                  {meta.masked && (
                    <span style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 11, color: "var(--muted)" }}>
                      {meta.masked}
                    </span>
                  )}
                  {meta.source === "db" && (
                    <button
                      onClick={() => clearOne(f.key)}
                      className="vbs-btn vbs-btn-ghost"
                      style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 10 }}
                      data-testid={`stripe-key-clear-${f.key}`}
                      title="Clear custom value, revert to .env fallback"
                    >
                      <Trash2 size={10} /> Reset to .env
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="vbs-input"
                    type={isShown ? "text" : "password"}
                    placeholder={f.placeholder}
                    value={draft}
                    onChange={(e) => setDraft(f.key, e.target.value)}
                    style={{ flex: 1, fontFamily: "var(--font-mono, monospace)", fontSize: 12 }}
                    data-testid={`stripe-key-input-${f.key}`}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => ({ ...s, [f.key]: !s[f.key] }))}
                    className="vbs-btn vbs-btn-ghost"
                    style={{ padding: "0 12px" }}
                    title={isShown ? "Hide" : "Show"}
                  >
                    {isShown ? <EyeOff size={12} /> : <Eye size={12} />}
                  </button>
                </div>
              </div>
            );
          })}

          <div
            style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              marginTop: 6, padding: "12px 14px",
              background: "rgba(201,168,76,0.05)", border: "1px solid rgba(201,168,76,0.18)",
              borderRadius: 4,
            }}
          >
            <ShieldCheck size={14} style={{ color: "var(--gold)" }} />
            <div style={{ fontSize: 12, color: "var(--mid)", flex: 1, lineHeight: 1.55 }}>
              Keys are stored in MongoDB (server-side only) and masked in the UI.
              Don't forget to also update the webhook URL in your Stripe dashboard if you create a fresh endpoint.
            </div>
            <button
              onClick={save}
              className="vbs-btn vbs-btn-gold"
              disabled={saving || dirtyCount === 0}
              data-testid="stripe-keys-save"
              style={{ minWidth: 180 }}
            >
              {saving ? (
                <><Loader2 size={12} className="animate-spin" /> Saving…</>
              ) : (
                <><Save size={12} /> Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
